/**
 * On-screen touch controls (missions-ux UI, order 758; docs/MOBILE.md). Shown only when the touch UI is
 * on (src/ui/mobile.ts decides) and the game is on the HUD — never over menus, result sheets or online
 * panels. Desktop is untouched: without touch the layer stays hidden and inert (it is built so that a
 * touch-screen laptop can switch it on with its first real finger, src/ui/mobile.ts).
 *
 *  - a floating analog stick on the left (right when left-handed) -> moveX/moveY on foot; in a car it
 *    points where to go and only steers (src/input/touchDrive.ts, the one touch driving scheme);
 *  - context buttons on the other side: on foot RUN / USE / HIT / AIM / SWAP, in a car the two equal GAS
 *    and BRAKE pedal zones (a thumb slides between them without lifting; BRAKE held past the stop backs
 *    up, with a REV badge), H-BRAKE, OUT, HORN, RADIO. On foot the stick's push is a stepless walk → jog
 *    (low-pass filtered so a thumb's wobble does not shake the body), RUN held is the only sprint, and AIM
 *    is held or tapped: a quick tap latches it on so the same thumb can FIRE, the next tap lets it go;
 *  - a small row of round system knobs in the top-right corner: MAP, PAUSE (+ whatever other modules
 *    add, e.g. the online MENU).
 *
 * Everything goes through `InputManager.setTouchButton / setTouchAxes` (src/input/input.ts): gameplay
 * reads the same actions and axes as from a keyboard or a pad. Finger bookkeeping and the stick maths
 * live in src/input/touch.ts (unit-tested).
 */
import { Vector3 } from 'three';
import './touch.css';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import type { ButtonAction } from '../input/actions';
import { DEFAULT_STICK, HoldOrLatch, RunLatch, StickFilter, TouchTracker, mapTouchAxes, touchUi, type TouchContext, type TouchControl } from '../input/touch';
import { TouchDrive, type Pedal } from '../input/touchDrive';
import { Layer } from '../physics/types';
import { cssSize } from '../render/cssSize';
import type { GameSettings } from '../save/saveData';
import { fullLockYawRate } from '../vehicles/model';
import { roadAxisAt } from '../world/roads';
import { el, setText, toggle } from './dom';
import { CONTROL_ICONS, weaponIcon, type ControlIcon } from './icons';
import { haptic, installMobileShell, isPortrait, type MobileShell } from './mobile';

export interface TouchSystemButton {
  id: string;
  label: string;
  run(): void;
  /** Hidden while this returns false (checked every frame). */
  visible?(): boolean;
}

export interface TouchControlsService {
  /** The touch UI is on (phone / tablet, or `?touch`). */
  readonly enabled: boolean;
  /** Fingers on the stick or a button right now (a tap elsewhere while one is down is not a tap). */
  readonly fingersDown: number;
  /** Add a button to the top system row (MAP / PAUSE / …). Returns a remover. */
  addSystemButton(b: TouchSystemButton): () => void;
  /**
   * A long press (HOLD_MS) on a system knob does `fn` instead of its tap (the BOOK knob held opens the
   * book on STREET online). Returns a remover.
   */
  onHold(id: string, fn: () => void): () => void;
}

/** A finger kept on a system knob this long is a hold, not a tap. */
export const HOLD_MS = 450;

declare module '../core/services' {
  interface ServiceMap {
    touchControls: TouchControlsService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Touch layer read-out: enabled, visible, context, held buttons, stick, axes pushed to input. */
    touchState(): Record<string, unknown>;
  }
}

export interface ButtonDef {
  /** Unique key (two buttons may drive the same action in different contexts). */
  key: string;
  label: string;
  /** Line icon (src/ui/icons.ts); 'weapon' = the silhouette of the weapon in hand. */
  icon: ControlIcon | 'weapon';
  ctx: Exclude<TouchContext, 'none'>;
  control: Exclude<TouchControl, { kind: 'stick' }>;
  /** Offset from the outer corner, in button units (--b): x from the side edge, y from the bottom. */
  x: number;
  y: number;
  /** Width / height in button units. */
  w: number;
  h?: number;
  tone?: 'red' | 'mustard';
  /** Round Bakelite knob (default), a ribbed pedal, or the handbrake lever. */
  shape?: 'gas' | 'brake' | 'lever';
  /** Secondary: smaller and quieter, outside the thumb's resting arc. */
  minor?: boolean;
}

const btn = (action: ButtonAction): TouchControl & { kind: 'button' } => ({ kind: 'button', action });

/**
 * The layout (right-handed; left-handed mirrors it). Units of --b (22 px x the size setting): 2 = 44 px,
 * the smallest target. The most used control is the biggest and sits in the corner under the resting
 * thumb; the next ones follow the thumb's arc around it; occasional ones sit outside the arc.
 *  - on foot: HIT (showing the weapon in hand) in the corner; AIM, USE, RUN on the arc; SWAP outside.
 *  - in a car: two equal pedal zones side by side, GAS in the corner and BRAKE / REVERSE beside it (the
 *    thumb slides between them without lifting), the H-BRAKE lever next to that; OUT above GAS, HORN and
 *    RADIO above BRAKE.
 */
export const TOUCH_BUTTONS: readonly ButtonDef[] = [
  // on foot
  { key: 'attack', label: 'Hit', icon: 'weapon', ctx: 'foot', control: btn('attack'), x: 0.5, y: 0.5, w: 3.4, tone: 'red' },
  { key: 'aim', label: 'Aim', icon: 'aim', ctx: 'foot', control: btn('aim'), x: 4.3, y: 0.35, w: 2.5 },
  { key: 'use', label: 'Use', icon: 'use', ctx: 'foot', control: btn('enterExit'), x: 3.5, y: 3.5, w: 2.2, tone: 'mustard' },
  { key: 'run', label: 'Run', icon: 'run', ctx: 'foot', control: btn('run'), x: 0.65, y: 4.25, w: 2.5 },
  // Only while a car and a place (a phone box, a door) are both in reach: USE takes the place, CAR the car.
  { key: 'car', label: 'Car', icon: 'car', ctx: 'foot', control: btn('vehicle'), x: 6, y: 4.9, w: 2, tone: 'mustard', minor: true },
  { key: 'weaponNext', label: 'Swap', icon: 'swap', ctx: 'foot', control: btn('weaponNext'), x: 6.75, y: 2.1, w: 2, minor: true },
  // in a car
  { key: 'gas', label: 'Gas', icon: 'gas', ctx: 'car', control: { kind: 'pedal', value: 1 }, x: 0.5, y: 0.5, w: 3.1, h: 4.4, tone: 'red', shape: 'gas' },
  { key: 'brake', label: 'Brake', icon: 'brake', ctx: 'car', control: { kind: 'pedal', value: -1 }, x: 3.75, y: 0.5, w: 3.1, h: 4.4, shape: 'brake' },
  { key: 'handbrake', label: 'H-brake', icon: 'handbrake', ctx: 'car', control: btn('handbrake'), x: 7.0, y: 0.5, w: 2, h: 2.9, shape: 'lever' },
  { key: 'out', label: 'Out', icon: 'out', ctx: 'car', control: btn('enterExit'), x: 0.75, y: 5.4, w: 2, tone: 'mustard', minor: true },
  { key: 'horn', label: 'Horn', icon: 'horn', ctx: 'car', control: btn('horn'), x: 3.9, y: 5.4, w: 2, minor: true },
  { key: 'radio', label: 'Radio', icon: 'radio', ctx: 'car', control: btn('radio'), x: 6.9, y: 5.4, w: 2, minor: true },
];

/** Icons for the system row; a button another module adds without one here shows its label. */
const SYS_ICONS: Record<string, ControlIcon> = { book: 'book', map: 'map', pause: 'pause', 'online-menu': 'menu' };
const SYS_BTN_PX = 44;
const SYS_GAP_PX = 6;

export const BUTTON_UNIT_PX = 22;
/** Smallest touch target (px): touch.css floors every control at this, growing small knobs inwards. */
export const MIN_TARGET_PX = 44;

/**
 * How far the button cluster reaches in from its side edge (px) at a touch size, both contexts
 * together: what other things low on the screen (the Add to Home Screen hint) keep clear of. Mirrors
 * touch.css (each control anchored at its outer corner, at least MIN_TARGET_PX).
 */
export function clusterWidthPx(touchSize: number): number {
  const u = BUTTON_UNIT_PX * touchSize;
  let w = 0;
  for (const b of TOUCH_BUTTONS) w = Math.max(w, b.x * u + Math.max(b.w * u, MIN_TARGET_PX));
  return Math.ceil(w);
}

/**
 * How far the button cluster reaches up from the bottom edge (px) at a touch size, both contexts
 * together (each control sits `y` units up and is at least MIN_TARGET_PX tall). Left-handed the cluster
 * is under the objective clipping's column, so the toasts stacked there stop above it (touch.css).
 */
export function clusterHeightPx(touchSize: number): number {
  const u = BUTTON_UNIT_PX * touchSize;
  let h = 0;
  for (const b of TOUCH_BUTTONS) h = Math.max(h, b.y * u + Math.max((b.h ?? b.w) * u, MIN_TARGET_PX));
  return Math.ceil(h);
}

/**
 * How far the resting stick (the ghost where the thumb lands) reaches in from its side edge (px) at a
 * touch size: touch.css `.tc-stick.idle` sits 0.5 button units in, 2 × the stick radius wide plus its
 * 2 px border on each side. The other side of what the Add to Home Screen hint keeps clear of.
 */
export function stickReachPx(touchSize: number): number {
  return Math.ceil(BUTTON_UNIT_PX * touchSize * 0.5 + DEFAULT_STICK.radius * touchSize * 2 + 4);
}

/** OUT's place in from the H-BRAKE lever when there is no room for it above GAS under the speedometer. */
export const OUT_ASIDE = { x: 9.2, y: 3.5 };

/**
 * Whether the OUT knob fits above GAS under the instrument slot (the speedometer) on a screen `heightPx`
 * tall, with the safe-area bottom inset, at a touch size. At 1.4× on a 343 px Safari screen the knob's
 * ring stood inside the dial; it then moves aside to OUT_ASIDE (touch.css `.out-aside`). `slotBottomPx`
 * is where the instrument slot ends (its top + the dial + the badge plate), from the top of the screen.
 */
export function outKnobAside(touchSize: number, heightPx: number, safeBottomPx: number, slotBottomPx: number): boolean {
  const u = BUTTON_UNIT_PX * touchSize;
  const out = TOUCH_BUTTONS.find((b) => b.key === 'out')!;
  const top = heightPx - (safeBottomPx + out.y * u + Math.max(out.w * u, MIN_TARGET_PX));
  return top < slotBottomPx + 6;
}

/**
 * Where OUT may stand, in order of preference (units of --b from the outer corner, mirrored left-handed):
 * above GAS, a step higher, in from the H-BRAKE lever (OUT_ASIDE), above RADIO, above HORN, in from the
 * lever's foot. At 1.4× on a phone the second place still touched the speedometer's ring, the online cargo
 * strip under the heat plate and, left-handed, the connection chip under the minimap (docs/UI-CONSISTENCY.md).
 */
export const OUT_SPOTS: readonly { x: number; y: number }[] = [{ x: 0.75, y: 5.4 }, { x: 0.75, y: 6.5 }, OUT_ASIDE, { x: 6.9, y: 7.7 }, { x: 3.9, y: 7.7 }, { x: 9.2, y: 0.5 }];

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The first of OUT_SPOTS where the knob is on screen and clear (by `gap` px) of every box in `obstacles`
 * (the HUD's plates and strips, the speedometer, the other knobs). `at` is the knob's box (px, screen) at
 * spot `from`; `b` px a unit; `lefty` mirrors x. When no spot is clear, the one with the least overlap.
 */
export function pickOutSpot(at: Box, from: number, b: number, lefty: boolean, obstacles: readonly Box[], screen: { w: number; h: number }, gap = 2): number {
  const f = OUT_SPOTS[from]!;
  let best = 0;
  let bestHit = Infinity;
  OUT_SPOTS.forEach((s, i) => {
    const dx = (s.x - f.x) * b * (lefty ? 1 : -1);
    const dy = -(s.y - f.y) * b;
    const k = { left: at.left + dx, right: at.right + dx, top: at.top + dy, bottom: at.bottom + dy };
    let hit = k.left < 0 || k.top < 0 || k.right > screen.w || k.bottom > screen.h ? 1e6 : 0;
    for (const o of obstacles) {
      const ox = Math.min(k.right, o.right) - Math.max(k.left, o.left) + gap;
      const oy = Math.min(k.bottom, o.bottom) - Math.max(k.top, o.top) + gap;
      if (ox > 0 && oy > 0) hit += ox * oy;
    }
    if (hit < bestHit) {
      bestHit = hit;
      best = i;
    }
  });
  return best;
}

/** HUD pieces OUT keeps clear of (the street plate under the minimap steps up for it instead: `.out-high`). */
const OUT_OBSTACLES = '.hud-vehicle:not(.is-hidden), .hud-cargo:not([hidden]), .hud-net:not([hidden]), .hud-money, .hud-heat, .hud-map, .hud-health:not([hidden])';

export function createTouchControlsSystem(): System {
  let ctx: GameContext;
  let shell: MobileShell | null = null;
  let layer: HTMLElement | null = null;
  let zone: HTMLElement | null = null;
  let stickEl: HTMLElement | null = null;
  let knobEl: HTMLElement | null = null;
  /** The direction cue on the car (a faint chevron where the thumb asks to go), only with a thumb on the stick in a car. */
  let cueEl: HTMLElement | null = null;
  let cueOn = false;
  const cueVec = new Vector3();
  let sysRow: HTMLElement | null = null;
  const buttonEls = new Map<string, HTMLElement>();
  const sysButtons: { def: TouchSystemButton; el: HTMLElement }[] = [];
  const tracker = new TouchTracker({ ...DEFAULT_STICK });
  const pointerKey = new Map<number, string>();
  const offs: (() => void)[] = [];
  let visible = false;
  let context: TouchContext = 'none';
  let lastAxes: Record<string, number> = {};
  // The one touch driving scheme (src/input/touchDrive.ts): the thumb's filtered heading, the reverse latch, the stuck assist.
  const drive = new TouchDrive();
  let reverseShown = false;
  let backingOff = false;
  // The pedals' boxes (px), for a thumb sliding from GAS to BRAKE and back; re-measured every few frames in a car.
  const pedalBox = new Map<string, DOMRect>();
  let pedalCheck = 0;
  let settings: Pick<GameSettings, 'touchSize' | 'touchOpacity' | 'touchLeftHanded' | 'touchHaptics' | 'steerSensitivity' | 'driveAssists'> = {
    touchSize: 1,
    touchOpacity: 0.75,
    touchLeftHanded: false,
    touchHaptics: true,
    steerSensitivity: 1,
    driveAssists: true,
  };
  let wasPortrait = false;
  // HIT shows the weapon in hand (and its rounds) — refreshed only when they change.
  let hitIcon: HTMLElement | null = null;
  let hitLabel: HTMLElement | null = null;
  let hitAmmo: HTMLElement | null = null;
  let hitWeapon = '';
  let sysShown = -1;
  const holds = new Map<string, () => void>();
  let outAside = false;
  let outSpot = 0;
  let outCheck = 0;
  // On foot: AIM and RUN held or latched by a tap (RUN's latch lets go when the stick is released); the
  // stick low-pass filtered (docs/CONTROLS-POLISH.md).
  const aimLatch = new HoldOrLatch();
  const runLatch = new RunLatch();
  const stickFilter = new StickFilter();

  const service: TouchControlsService = {
    get enabled() {
      return touchUi.enabled;
    },
    get fingersDown() {
      return pointerKey.size;
    },
    addSystemButton(def) {
      const entry = { def, el: makeSysButton(def) };
      sysButtons.push(entry);
      return () => {
        const i = sysButtons.indexOf(entry);
        if (i >= 0) sysButtons.splice(i, 1);
        entry.el.remove();
      };
    },
    onHold(id, fn) {
      holds.set(id, fn);
      return () => {
        if (holds.get(id) === fn) holds.delete(id);
      };
    },
  };

  // ------------------------------------------------------------------ DOM

  function build(root: HTMLElement): void {
    layer = el('div', 'bh-ui bh-tc-layer is-hidden', undefined, root);
    layer.style.pointerEvents = 'none';
    zone = el('div', 'tc-zone', undefined, layer);
    zone.dataset.tc = 'stick';
    stickEl = el('div', 'tc-stick idle', undefined, layer);
    knobEl = el('div', 'tc-knob', undefined, stickEl);
    cueEl = el('div', 'tc-cue', undefined, layer);
    const pad = el('div', 'tc-pad', undefined, layer);
    for (const b of TOUCH_BUTTONS) {
      const cls = ['tc-btn', `ctx-${b.ctx}`, b.tone, b.control.kind === 'pedal' ? 'pedal' : '', b.shape ? `shape-${b.shape}` : '', b.minor ? 'minor' : ''];
      const e = el('div', cls.filter(Boolean).join(' '), undefined, pad);
      const ico = el('span', 'ico', undefined, e);
      ico.innerHTML = b.icon === 'weapon' ? weaponIcon('fists', 'unarmed') : CONTROL_ICONS[b.icon];
      const lbl = el('span', 'lbl', b.label, e);
      // BRAKE held past the stop backs the car up: the badge says so (`.rev`, with the reverse lights).
      if (b.key === 'brake') el('b', 'rev', 'Rev', e);
      if (b.icon === 'weapon') {
        hitIcon = ico;
        hitLabel = lbl;
        hitAmmo = el('b', 'ammo', '', e);
        hitAmmo.hidden = true;
      }
      e.dataset.tc = b.key;
      e.setAttribute('role', 'button');
      e.setAttribute('aria-label', b.label);
      e.style.setProperty('--x', String(b.x));
      e.style.setProperty('--y', String(b.y));
      e.style.setProperty('--w', String(b.w));
      e.style.setProperty('--h', String(b.h ?? b.w));
      buttonEls.set(b.key, e);
    }
    sysRow = el('div', 'tc-sys', undefined, layer);
    service.addSystemButton({ id: 'map', label: 'Map', run: () => ctx.input.pulse('map') });
    service.addSystemButton({ id: 'pause', label: 'Pause', run: () => ctx.input.pulse('pause') });
    layer.addEventListener('pointerdown', onDown);
    // No click after a touch that began on the controls: the browser aims it at whatever is under the
    // finger when it lifts, and by then USE / MENU may have opened a panel there — whose backdrop would
    // take the click as "close".
    layer.addEventListener('touchend', onTouchEnd, { passive: false });
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    offs.push(() => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    });
  }

  function makeSysButton(def: TouchSystemButton): HTMLElement {
    const icon = SYS_ICONS[def.id];
    const b = el('button', `tc-sys-btn sys-${def.id}${icon ? ' has-icon' : ''}`, icon ? undefined : def.label, sysRow ?? undefined);
    b.type = 'button';
    if (icon) {
      b.innerHTML = CONTROL_ICONS[icon];
      b.setAttribute('aria-label', def.label);
      b.title = def.label;
    }
    const fire = (): void => {
      if (settings.touchHaptics) haptic(8);
      def.run();
    };
    // A finger fires on lift, not on `click`: browsers send no click for a tap made while another
    // finger is down — and a thumb is nearly always on the stick or GAS when PAUSE / MAP is tapped.
    // Kept down HOLD_MS it is a hold instead (`onHold`), and the lift then does nothing.
    let armed: number | null = null;
    let firedAt = -Infinity;
    let holdTimer: number | null = null;
    const clearHold = (): void => {
      if (holdTimer !== null) window.clearTimeout(holdTimer);
      holdTimer = null;
    };
    b.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      e.preventDefault();
      armed = e.pointerId;
      clearHold();
      if (holds.has(def.id)) {
        holdTimer = window.setTimeout(() => {
          holdTimer = null;
          if (armed === null) return;
          armed = null;
          firedAt = performance.now();
          if (settings.touchHaptics) haptic(18);
          holds.get(def.id)?.();
        }, HOLD_MS);
      }
    });
    b.addEventListener('pointerup', (e) => {
      clearHold();
      if (e.pointerId !== armed) return;
      armed = null;
      firedAt = performance.now();
      fire();
    });
    b.addEventListener('pointercancel', () => {
      clearHold();
      armed = null;
    });
    b.addEventListener('click', (e) => {
      e.preventDefault();
      if (performance.now() - firedAt < 700) return; // the click that may trail a finger lift
      fire(); // mouse, keyboard, assistive tech
    });
    return b;
  }

  function applySettings(s: GameSettings): void {
    settings = {
      touchSize: s.touchSize,
      touchOpacity: s.touchOpacity,
      touchLeftHanded: s.touchLeftHanded,
      touchHaptics: s.touchHaptics,
      steerSensitivity: s.steerSensitivity,
      driveAssists: s.driveAssists,
    };
    tracker.cfg.radius = DEFAULT_STICK.radius * s.touchSize;
    // Things low on the screen outside the layer (the Add to Home Screen hint) keep clear of the cluster.
    setClusterW(clusterWidthPx(s.touchSize));
    ctx?.uiRoot?.style.setProperty('--tc-cluster-h', `${clusterHeightPx(s.touchSize)}px`);
    ctx?.uiRoot?.style.setProperty('--tc-stick-w', `${stickReachPx(s.touchSize)}px`);
    if (!layer) return;
    layer.style.setProperty('--b', `${(BUTTON_UNIT_PX * s.touchSize).toFixed(1)}px`);
    layer.style.setProperty('--tc-op', String(s.touchOpacity));
    layer.style.setProperty('--stick-r', `${tracker.cfg.radius.toFixed(1)}px`);
    toggle(layer, 'lefty', s.touchLeftHanded);
    outCheck = 0;
    pedalCheck = 0;
  }

  /**
   * OUT above GAS meets the speedometer on a short screen at a big touch size: measured from the live
   * layout (the knob row's foot is the slot's top; the dial is --dial; the badge plate under it ~22 px),
   * every few frames in a car, and the knob moves aside while it would.
   */
  function placeOutKnob(): void {
    if (!layer || !sysRow) return;
    if (context !== 'car') {
      setClusterW(clusterWidthPx(settings.touchSize)); // OUT is hidden on foot: the cluster's own reach
      return;
    }
    if (outCheck-- > 0) return;
    outCheck = 30;
    const H = layer.clientHeight;
    if (!H) return;
    const layerTop = layer.getBoundingClientRect().top;
    // The speedometer with its badge plate, as drawn (it is up whenever OUT is); the slot's nominal
    // size as the fallback.
    const dialEl = ctx.uiRoot?.querySelector('.hud-vehicle:not(.is-hidden)');
    const dialRect = dialEl?.getBoundingClientRect();
    const row = sysRow.getBoundingClientRect();
    const slotBottom = dialRect && dialRect.height > 0 ? dialRect.bottom - layerTop : row.bottom - layerTop + 8 + Math.max(84, Math.min(136, H * 0.27)) + 22;
    const saB = parseFloat(getComputedStyle(layer).getPropertyValue('--sa-b')) || 0;
    // Left-handed the cluster is on the left and the dial stays top right: OUT is nowhere near it (moved
    // aside it stood on the objective clipping instead).
    const aside = !settings.touchLeftHanded && outKnobAside(settings.touchSize, H, saB, slotBottom);
    // Left-handed at a big touch size OUT (above GAS, on the left) reaches the street plate under the
    // minimap: the plate steps up onto the map's rim while it does (touch.css `.out-high`). Measured
    // against the plate's natural place, not where it is now, so it doesn't flap.
    const outEl = buttonEls.get('out');
    const mapEl = ctx.uiRoot?.querySelector('.hud-map');
    const plateEl = ctx.uiRoot?.querySelector('.hud-map .street');
    if (settings.touchLeftHanded && outEl && mapEl && plateEl) {
      const o = outEl.getBoundingClientRect();
      const m = mapEl.getBoundingClientRect();
      const s = plateEl.getBoundingClientRect();
      const plateBottom = m.bottom + 4 + s.height;
      toggle(layer, 'out-high', o.width > 0 && s.width > 0 && o.left < m.left + s.width && o.top < plateBottom + 2);
    } else toggle(layer, 'out-high', false);
    // Then the place that is clear of everything drawn there, from the live layout. The first guess is the
    // dial rule above (cheap, and right on every screen it was written for); the measured pick has the last word.
    const out = buttonEls.get('out');
    if (!out) return;
    let spot = aside ? OUT_SPOTS.indexOf(OUT_ASIDE) : 0;
    const r = out.getBoundingClientRect();
    if (r.width > 0) {
      const lefty = settings.touchLeftHanded;
      const obstacles: Box[] = [];
      for (const e of ctx.uiRoot?.querySelectorAll<HTMLElement>(OUT_OBSTACLES) ?? []) {
        const q = e.getBoundingClientRect();
        if (q.width > 0 && q.height > 0 && getComputedStyle(e).visibility !== 'hidden' && +getComputedStyle(e).opacity > 0.05) obstacles.push(q);
      }
      for (const [key, e] of buttonEls) {
        if (key === 'out') continue;
        const q = e.getBoundingClientRect();
        if (q.width > 0) obstacles.push(q);
      }
      if (sysRow) obstacles.push(sysRow.getBoundingClientRect());
      spot = pickOutSpot(r, outSpot, BUTTON_UNIT_PX * settings.touchSize, lefty, obstacles, { w: window.innerWidth, h: window.innerHeight });
    }
    // The band between the thumbs (tip slip, law strip, the home-screen hint) keeps clear of where OUT went.
    const def = TOUCH_BUTTONS.find((d) => d.key === 'out')!;
    setClusterW(Math.max(clusterWidthPx(settings.touchSize), Math.ceil((OUT_SPOTS[spot]!.x + def.w) * BUTTON_UNIT_PX * settings.touchSize)));
    if (spot === outSpot) return;
    outSpot = spot;
    outAside = spot !== 0;
    toggle(layer, 'out-aside', outAside);
    const p = OUT_SPOTS[spot]!;
    out.style.setProperty('--x', String(p.x));
    out.style.setProperty('--y', String(p.y));
  }

  /** --tc-cluster-w on the UI root, written only when it changes. */
  let clusterW = 0;
  function setClusterW(px: number): void {
    if (px === clusterW) return;
    clusterW = px;
    ctx?.uiRoot?.style.setProperty('--tc-cluster-w', `${px}px`);
  }

  // ------------------------------------------------------------------ pointers

  /** Hold or let go of a button action from every source the layer has: fingers, AIM's and RUN's latch. */
  function syncButton(a: ButtonAction): void {
    const on = tracker.isHeld(a) || (a === 'aim' && aimLatch.on) || (a === 'run' && runLatch.on);
    ctx.input.setTouchButton(a, on);
    const key = a === 'run' ? 'run' : a === 'aim' ? 'aim' : null;
    const e = key ? buttonEls.get(key) : undefined;
    if (e && key) {
      toggle(e, 'on', on);
      if (a === 'aim') toggle(e, 'latched', aimLatch.latched);
      if (a === 'run') toggle(e, 'latched', runLatch.latched);
    }
  }

  function controlFor(key: string): TouchControl | null {
    if (key === 'stick') return { kind: 'stick' };
    return TOUCH_BUTTONS.find((b) => b.key === key)?.control ?? null;
  }

  function onDown(e: PointerEvent): void {
    if (!visible) return;
    const target = (e.target as Element | null)?.closest('[data-tc]') as HTMLElement | null;
    const key = target?.dataset.tc;
    if (!key) return;
    const control = controlFor(key);
    if (!control) return;
    e.preventDefault(); // no emulated mouse events, no focus moves, no text selection
    if (!tracker.down(e.pointerId, control, e.clientX, e.clientY)) return;
    pointerKey.set(e.pointerId, key);
    if (control.kind === 'button' && control.action === 'aim') aimLatch.down(performance.now());
    if (control.kind === 'button' && control.action === 'run') runLatch.down(performance.now());
    if (control.kind === 'button') ctx.input.setTouchButton(control.action, true);
    if (control.kind !== 'stick') {
      const be = buttonEls.get(key)!;
      toggle(be, 'on', true);
      // a short press ripple (restarted on every press), the visual half of the haptic tick
      be.classList.remove('ping');
      void be.offsetWidth;
      be.classList.add('ping');
      if (settings.touchHaptics) haptic(control.kind === 'pedal' ? 6 : 10);
    }
    drawStick();
  }

  function onTouchEnd(e: TouchEvent): void {
    if (e.cancelable) e.preventDefault();
  }

  function onMove(e: PointerEvent): void {
    if (!tracker.owns(e.pointerId)) return;
    tracker.move(e.pointerId, e.clientX, e.clientY);
    slidePedal(e.pointerId, e.clientX, e.clientY);
    drawStick();
  }

  function onUp(e: PointerEvent): void {
    if (!tracker.owns(e.pointerId)) return;
    release(e.pointerId);
    drawStick();
  }

  /**
   * A thumb on GAS that slides onto BRAKE (or back) takes the other pedal without lifting: the exception
   * to "a finger keeps the control it landed on", so braking into a corner and getting back on the power
   * is one thumb rolling sideways. Any other control keeps its finger as before.
   */
  function slidePedal(id: number, x: number, y: number): void {
    const key = pointerKey.get(id);
    if (key !== 'gas' && key !== 'brake') return;
    const other = key === 'gas' ? 'brake' : 'gas';
    const box = pedalBox.get(other);
    if (!box || x < box.left || x > box.right || y < box.top || y > box.bottom) return;
    const control = controlFor(other);
    if (!control || !tracker.down(id, control, x, y)) return;
    pointerKey.set(id, other);
    if (![...pointerKey.values()].includes(key)) toggle(buttonEls.get(key)!, 'on', false);
    toggle(buttonEls.get(other)!, 'on', true);
    if (settings.touchHaptics) haptic(6);
  }

  /** Where the pedals are drawn (px), measured every few frames while in a car (they only move with the settings). */
  function measurePedals(): void {
    if (context !== 'car' || pedalCheck-- > 0) return;
    pedalCheck = 30;
    for (const key of ['gas', 'brake']) {
      const r = buttonEls.get(key)?.getBoundingClientRect();
      if (r && r.width > 0) pedalBox.set(key, r);
    }
  }

  function release(id: number): void {
    const c = tracker.up(id);
    const key = pointerKey.get(id);
    pointerKey.delete(id);
    if (c?.kind === 'button' && c.action === 'aim' && !tracker.isHeld('aim')) aimLatch.up(performance.now());
    if (c?.kind === 'button' && c.action === 'run' && !tracker.isHeld('run')) runLatch.up(performance.now());
    if (key && key !== 'stick' && ![...pointerKey.values()].includes(key)) toggle(buttonEls.get(key)!, 'on', false);
    if (c?.kind === 'button') syncButton(c.action);
    // The stick thumb lifting ends a latched run: a tapped RUN lasts one push, never into the next one.
    if (c?.kind === 'stick') {
      runLatch.stickReleased();
      syncButton('run');
    }
  }

  function releaseAll(): void {
    drive.reset();
    showReverse(false, false);
    aimLatch.reset();
    runLatch.reset();
    stickFilter.reset();
    for (const id of [...pointerKey.keys()]) release(id);
    tracker.releaseAll();
    ctx.input.clearTouch();
    lastAxes = {};
    drawStick();
  }

  function drawStick(): void {
    if (!stickEl || !knobEl) return;
    const active = tracker.stickActive;
    toggle(stickEl, 'idle', !active);
    if (!active) {
      stickEl.style.transform = '';
      knobEl.style.transform = '';
      return;
    }
    const b = tracker.stickBase;
    const k = tracker.stickKnob;
    stickEl.style.transform = `translate(${b.x.toFixed(1)}px, ${b.y.toFixed(1)}px)`;
    knobEl.style.transform = `translate(${(k.x - b.x).toFixed(1)}px, ${(k.y - b.y).toFixed(1)}px)`;
  }

  // ------------------------------------------------------------------ per frame

  /** How close (m) in front of the bumper the stuck assist looks for a wall, and how slow the car must be. */
  const NOSE_REACH = 0.5;
  const NOSE_PROBE_SPEED = 0.6;

  /**
   * Static world geometry against the bumper (a wall, a building, a fixed post): three short rays from
   * the front corners and the middle, only while crawling with GAS down. A car or a person in the way is
   * not a wall — the stuck assist never backs off those. `lateral` is the wall normal's share along the
   * car's right.
   */
  function noseBlocked(v: NonNullable<ReturnType<typeof carVehicle>>): { lateral: number } | null {
    if (!tracker.pedalHeld() || tracker.pedal() <= 0 || v.speed > NOSE_PROBE_SPEED) return null;
    const sinH = Math.sin(v.heading);
    const cosH = Math.cos(v.heading);
    const rx = -cosH;
    const rz = sinH;
    const nose = v.spec.length / 2 + 0.05;
    let best: { lateral: number } | null = null;
    let bestD = Infinity;
    for (const k of [0, -0.8, 0.8]) {
      const off = (v.spec.width / 2) * k;
      const origin = { x: v.pos.x + sinH * nose + rx * off, z: v.pos.z + cosH * nose + rz * off };
      const hit = ctx.physics.raycast(origin, { x: sinH, z: cosH }, NOSE_REACH, { mask: Layer.VEHICLE | Layer.PED | Layer.PLAYER | Layer.PROP, ignore: { id: v.body.id } });
      if (!hit || hit.distance >= bestD) continue;
      bestD = hit.distance;
      best = hit.bodyId === null ? { lateral: hit.normal.x * rx + hit.normal.z * rz } : null;
    }
    return best;
  }

  function carVehicle() {
    const p = ctx.services.tryGet('player');
    return p?.mode === 'driving' ? p.vehicle : null;
  }

  /**
   * Driving axes (src/input/touchDrive.ts): the stick points where to go on the screen, the pedals own the
   * throttle and the reverse. Needs the car we drive and the camera's screen axes; with assists the road
   * axis under the car and whether the bumper is against a wall (docs/DRIVING.md §8).
   */
  function carAxes(dt: number): ReturnType<typeof mapTouchAxes> | null {
    if (context !== 'car') return null;
    const v = carVehicle();
    const rig = ctx.services.tryGet('cameraRig');
    if (!v || !rig) return null;
    const speed = v.speed;
    const layout = settings.driveAssists ? ctx.services.tryGet('layout') : undefined;
    const roadAxis = layout && speed > 3 ? roadAxisAt(layout, v.pos) : null;
    const forwardSpeed = v.velocity.x * Math.sin(v.heading) + v.velocity.z * Math.cos(v.heading);
    const pedal: Pedal = tracker.pedalHeld() ? (tracker.pedal() < 0 ? 'brake' : 'gas') : null;
    const facts = { heading: v.heading, forwardSpeed, speed, screenUp: rig.screenUp(), screenRight: rig.screenRight(), yawRate: v.body.angVel, fullLockYaw: fullLockYawRate(v.spec, speed), roadAxis, x: v.pos.x, z: v.pos.z, noseBlocked: settings.driveAssists ? noseBlocked(v) : null };
    const d = drive.step(tracker.stick, pedal, facts, dt, { sensitivity: settings.steerSensitivity, assists: settings.driveAssists });
    showReverse(d.reverse, d.backingOff);
    return { moveX: 0, moveY: 0, throttle: d.throttle, steer: d.steer };
  }

  /** The REV badge on the BRAKE pedal (and a tick) while reverse is engaged; `.backing` while the stuck assist drives. */
  function showReverse(on: boolean, backing: boolean): void {
    if (on === reverseShown && backing === backingOff) return;
    if (on && !reverseShown && settings.touchHaptics) haptic(10);
    reverseShown = on;
    backingOff = backing;
    const e = buttonEls.get('brake');
    if (e) {
      toggle(e, 'rev', on);
      toggle(e, 'backing', backing);
    }
  }

  function currentContext(): TouchContext {
    const p = ctx.services.tryGet('player');
    if (!p) return 'none';
    if (p.mode === 'driving' || p.mode === 'riding') return 'car';
    if (p.mode === 'foot') return 'foot';
    return context === 'none' ? 'foot' : context; // entering / exiting / knocked: keep the last layout
  }

  function updateHitButton(): void {
    if (context !== 'foot' || !hitIcon || !hitLabel || !hitAmmo) return;
    const w = ctx.services.tryGet('weapons');
    if (!w) return;
    const d = w.def;
    if (d.id !== hitWeapon) {
      hitWeapon = d.id;
      hitIcon.innerHTML = weaponIcon(d.id, d.cls);
      setText(hitLabel, d.firearm ? 'Fire' : 'Hit');
      buttonEls.get('attack')?.setAttribute('aria-label', d.firearm ? `Fire ${d.name}` : `Hit (${d.name})`);
    }
    hitAmmo.hidden = !d.firearm;
    if (d.firearm) {
      setText(hitAmmo, String(w.loaded));
      toggle(hitAmmo, 'empty', w.loaded === 0);
    }
  }

  function shouldShow(): boolean {
    if (!touchUi.enabled || !layer) return false;
    if (ctx.state.mode !== 'playing') return false;
    const ui = ctx.services.tryGet('ui');
    if (!ui || ui.screen !== 'hud') return false;
    if (ctx.services.tryGet('online')?.panels.isOpen) return false;
    // The intro note / online welcome (src/onboarding) has the player's attention for a few seconds.
    if (ctx.services.tryGet('onboarding')?.introOpen) return false;
    return !isPortrait();
  }

  function frame(dt: number): void {
    // Desktop: the (hidden) layer exists in case a first real touch switches the touch UI on; until
    // then there is nothing to do per frame.
    if (!layer || (!touchUi.enabled && !visible)) return;
    // Turned to portrait mid-game: offline, pause (the card asks to turn it back).
    const portrait = touchUi.enabled && isPortrait();
    if (portrait && !wasPortrait && ctx.state.mode === 'playing' && ctx.services.tryGet('ui')?.screen === 'hud' && !ctx.services.tryGet('net')?.online) {
      ctx.hooks.call('pause', true);
    }
    wasPortrait = portrait;

    const show = shouldShow();
    if (show !== visible) {
      visible = show;
      toggle(layer, 'is-hidden', !show);
      if (!show) releaseAll();
    }
    // The CAR knob: a car to get into while USE points at something else.
    const carEl = buttonEls.get('car');
    if (carEl) {
      const p = ctx.services.tryGet('player');
      const here = ctx.services.tryGet('interactions')?.current;
      const want = !!p && p.mode === 'foot' && !!p.enterCandidate && !!here && !here.id.startsWith('vehicle:');
      if (carEl.hidden === want) carEl.hidden = !want;
    }
    let shownSys = 0;
    for (const s of sysButtons) {
      s.el.hidden = !(s.def.visible?.() ?? true);
      if (!s.el.hidden) shownSys++;
    }
    if (shownSys !== sysShown && ctx.uiRoot) {
      // The cash / heat plates sit left of the system row: tell the HUD how wide the row is.
      sysShown = shownSys;
      const w = `${shownSys * SYS_BTN_PX + Math.max(0, shownSys - 1) * SYS_GAP_PX}px`;
      ctx.uiRoot.style.setProperty('--tc-sys-w', w);
      // touch.css declares the 94 px default on `.bh-ui` itself, which shadows the root's value: set it
      // where the plates read it, or a third knob (BOOK, online MENU) sits on top of cash / heat.
      ctx.uiRoot.querySelectorAll<HTMLElement>('.bh-ui').forEach((e) => e.style.setProperty('--tc-sys-w', w));
    }
    if (!show) return;
    updateHitButton();
    const c = currentContext();
    if (c !== context) {
      // Swapping layouts: let go of buttons that are about to disappear (keep the stick), and of the latches.
      aimLatch.reset();
      runLatch.reset();
      stickFilter.reset();
      for (const [id, key] of [...pointerKey]) if (key !== 'stick') release(id);
      drive.reset();
      showReverse(false, false);
      pedalCheck = 0;
      syncButton('aim');
      syncButton('run');
      context = c;
      toggle(layer, 'ctx-car', c === 'car');
      toggle(layer, 'ctx-foot', c === 'foot');
      outCheck = 0;
    }
    placeOutKnob();
    measurePedals();
    // On foot the stick is low-pass filtered (a thumb's wobble must not shake the body); sprinting is RUN only.
    // In a car without the facts yet (riding, the first frames): the pedals alone, no steer.
    const stick = context === 'foot' ? stickFilter.step(tracker.stick, dt) : { x: 0, y: 0, mag: 0 };
    const axes = carAxes(dt) ?? mapTouchAxes(context, stick, tracker.pedal(), tracker.pedalHeld());
    ctx.input.setTouchAxes(axes);
    lastAxes = axes;
    drawCue();
  }

  /** How far (px) from the car's centre the direction cue sits. */
  const CUE_RADIUS = 44;

  /**
   * The direction cue: a faint chevron beside the car pointing where the thumb asks to go (the filtered
   * heading the steer works from, docs/DRIVING.md §8), only while a thumb is on the stick in a car.
   * Two projections and one transform a frame; hidden otherwise.
   */
  function drawCue(): void {
    if (!cueEl) return;
    const v = context === 'car' && tracker.stickActive ? carVehicle() : null;
    const want = drive.wantHeading;
    if (!v || !ctx.render || Number.isNaN(want)) {
      if (cueOn) {
        toggle(cueEl, 'on', false);
        cueOn = false;
      }
      return;
    }
    const cam = ctx.camera;
    const { w, h } = cssSize(ctx.render.canvas);
    cueVec.set(v.pos.x, 0.8, v.pos.z).project(cam);
    const sx = ((cueVec.x + 1) / 2) * w;
    const sy = ((1 - cueVec.y) / 2) * h;
    cueVec.set(v.pos.x + Math.sin(want) * 2, 0.8, v.pos.z + Math.cos(want) * 2).project(cam);
    const ang = Math.atan2(((1 - cueVec.y) / 2) * h - sy, ((cueVec.x + 1) / 2) * w - sx);
    cueEl.style.transform = `translate(${(sx + Math.cos(ang) * CUE_RADIUS).toFixed(1)}px, ${(sy + Math.sin(ang) * CUE_RADIUS).toFixed(1)}px) rotate(${((ang * 180) / Math.PI).toFixed(1)}deg)`;
    if (!cueOn) {
      toggle(cueEl, 'on', true);
      cueOn = true;
    }
  }

  return {
    id: 'touch-controls',
    order: 758,
    init(c) {
      ctx = c;
      c.services.provide('touchControls', service);
      c.hooks.register('touchState', () => ({
        enabled: touchUi.enabled,
        visible,
        context,
        fingers: tracker.fingers,
        stick: tracker.stick,
        buttons: [...tracker.heldButtons()],
        aimLatched: aimLatch.latched,
        runLatched: runLatch.latched,
        pedal: tracker.pedal(),
        reverse: reverseShown,
        backingOff,
        wantHeading: drive.wantHeading,
        axes: lastAxes,
        // Screen-relative steering checks: the car's heading and the screen's right on the ground.
        carHeading: ctx.services.tryGet('player')?.vehicle?.heading ?? null,
        screenRight: ctx.services.tryGet('cameraRig')?.screenRight() ?? null,
        input: c.input.touchState(),
        settings,
        outAside,
        standalone: typeof document !== 'undefined' && document.documentElement.classList.contains('bh-standalone'),
        homeScreenHint: !!shell?.hint,
      }));
      if (!c.uiRoot || typeof document === 'undefined') return;
      shell = installMobileShell(c.uiRoot);
      build(c.uiRoot);
      offs.push(
        c.events.on('settingsChanged', (e) => applySettings(e.settings)),
        // Haptics for knocks you feel: a hard crash in your car, getting hurt on foot.
        c.events.on('collision', (e) => {
          if (!visible || !settings.touchHaptics || e.speed < 9) return;
          const veh = c.services.tryGet('player')?.vehicle;
          if (veh && ((e.a.kind === 'vehicle' && e.a.id === veh.id) || (e.b?.kind === 'vehicle' && e.b.id === veh.id))) haptic(Math.min(60, 15 + e.speed * 2));
        }),
        c.events.on('playerHurt', () => {
          if (visible && settings.touchHaptics) haptic([30, 40, 30]);
        }),
        touchUi.onChange(() => (visible ? releaseAll() : undefined)),
      );
      const hide = (): void => {
        if (document.visibilityState === 'hidden') releaseAll();
      };
      document.addEventListener('visibilitychange', hide);
      window.addEventListener('blur', releaseAll);
      offs.push(() => {
        document.removeEventListener('visibilitychange', hide);
        window.removeEventListener('blur', releaseAll);
      });
    },
    start() {
      const save = ctx.services.tryGet('save');
      if (save) applySettings(save.settings);
    },
    update(dt) {
      frame(dt);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      layer?.removeEventListener('pointerdown', onDown);
      layer?.removeEventListener('touchend', onTouchEnd);
      layer?.remove();
      shell?.dispose();
    },
  };
}
